import React, { useContext, useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import PieChartComponent from './PieChartComponent';
import GaugeChartComponent from './GaugeChartComponent';
import LineChartComponent from './LineChartComponent';
import ModalDialog from './ModalDialog';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../AuthContext';
import { LanguageContext } from '../LanguageContext';
import styles from './Dashboard.module.css'; 

const Dashboard = () => {
  const getCompactLayout = () => window.innerWidth < 1100 || window.innerHeight > window.innerWidth;
  const { authToken, logout } = useContext(AuthContext);
  const { language, setLanguage, t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [isCompactLayout, setIsCompactLayout] = useState(getCompactLayout());
  const [renameModal, setRenameModal] = useState(null);
  const [messageModal, setMessageModal] = useState(null);
  const [passwordModal, setPasswordModal] = useState(null);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      setIsCompactLayout(getCompactLayout());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authToken) {
      navigate('/login');
    }
  }, [authToken, navigate]);

  useEffect(() => {
    const fetchMe = async () => {
      try {
        const response = await api.get('/me/');
        setMe(response.data);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchMe();
  }, []);

  // Fetch devices from API
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await api.get('/devices/');
        setDevices(response.data);
        if (response.data.length > 0 && selectedDevice === null) {
          setSelectedDevice(response.data[0].id); // Select the first device by default
        }
      } catch (error) {
        console.error('Error fetching devices:', error);
      }
    };
    fetchDevices();
    const timer = setInterval(fetchDevices, 300000);
    return () => clearInterval(timer);
  }, [selectedDevice]);

  // Fetch chart data for the selected device
  useEffect(() => {
    if (selectedDevice !== null) {
      const fetchData = async () => {
        try {
          try {
            const response = await api.get(`/devices/${selectedDevice}/charts-v3/`);
            setChartData(response.data);
          } catch (v3Error) {
            const response = await api.get(`/devices/${selectedDevice}/charts/`);
            setChartData(response.data);
          }
        } catch (error) {
          console.error('Error fetching chart data:', error);
        }
      };
      fetchData();
      const timer = setInterval(fetchData, 300000);
      return () => clearInterval(timer);
    }
  }, [selectedDevice]);

  const StateLabels = useMemo(() => ({
    0: t('no_proces'),
    1: t('process_recognition'),
    2: t('r_proces'),
    3: t('process_unrecognized'),
    4: t('cleaning'),
    5: t('cleaning'),
    6: t('off'),
    7: t('off'),
  }), [t]);

  // Show dashboard with placeholder device while loading
  const displayDevices = devices.length > 0 ? devices : [{ id: 0, name: t('loading') }];
  const currentSelectedDevice = devices.length > 0 ? selectedDevice : null;
  const isAdmin = !!me?.is_superuser;
  const trendChartTitleKey = isAdmin ? 'cleaning_pressure' : 'filter_usage_history';
  const trendChartData = isAdmin ? (chartData.DataPressure2 || []) : (chartData.DataFilterUsage || []);
  const trendChartRegression = isAdmin
    ? (chartData.DataPressure2Regression || [])
    : (chartData.DataFilterUsageRegression || []);
  const dailyCompletedProcessData = chartData.DataDailyCompletedProcesses || [];
  const currentFilterUsage = Math.max(0, Number(chartData.hepa_actual || 0));

  const renameSelectedFilter = async () => {
    if (selectedDevice === null) {
      return;
    }

    const currentName = devices.find((d) => d.id === selectedDevice)?.name || '';
    setRenameModal({
      value: currentName,
      error: '',
    });
  };

  const submitRenameSelectedFilter = async () => {
    if (selectedDevice === null || !renameModal) {
      return;
    }
    const nextName = (renameModal.value || '').trim();
    if (!nextName) {
      setRenameModal((prev) => ({ ...prev, error: t('filterNameRequired') }));
      return;
    }

    try {
      await api.patch(`/devices/${selectedDevice}/rename/`, { name: nextName });
      const response = await api.get('/devices/');
      setDevices(response.data);
      setRenameModal(null);
      setMessageModal({ title: t('renameFilter'), message: t('renameSuccess') });
    } catch (error) {
      console.error('Error renaming filter:', error);
      setRenameModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  const openPasswordModal = () => {
    setPasswordModal({
      password: '',
      confirmPassword: '',
      error: '',
    });
  };

  const submitPasswordChange = async () => {
    if (!passwordModal) {
      return;
    }
    if (!passwordModal.password || !passwordModal.confirmPassword) {
      setPasswordModal((prev) => ({ ...prev, error: t('passwordRequired') }));
      return;
    }
    if (passwordModal.password !== passwordModal.confirmPassword) {
      setPasswordModal((prev) => ({ ...prev, error: t('passwordMismatch') }));
      return;
    }

    try {
      await api.patch('/me/password/', { password: passwordModal.password });
      setPasswordModal(null);
      setMessageModal({ title: t('changePassword'), message: t('passwordChanged') });
    } catch (error) {
      console.error('Error changing current user password:', error);
      setPasswordModal((prev) => ({ ...prev, error: t('saveError') }));
    }
  };

  // Function to check if timestamp is older than 6 hours
  const isOlderThan6Hours = (timestamp) => {
    if (!timestamp) return false;
    const now = new Date();
    const lastTime = new Date(timestamp);
    const diffInHours = (now - lastTime) / (1000 * 60 * 60);
    return diffInHours > 6;
  };

  // Function to get time since last communication
  const getTimeSinceLastCommunication = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const lastTime = new Date(timestamp);
    const diffInHours = (now - lastTime) / (1000 * 60 * 60);
    
    if (diffInHours < 6) return '';
    
    if (diffInHours >= 24) {
      const days = Math.floor(diffInHours / 24);
      const hours = Math.floor(diffInHours % 24);
      if (days === 1) {
        return hours > 0 ? ` (${t('noCommunicationSince')} ${days} ${t('day')} ${hours} ${getHourUnit(hours)})` : ` (${t('noCommunicationSince')} ${days} ${t('day')})`;
      } else {
        return hours > 0 ? ` (${t('noCommunicationSince')} ${days} ${t('days')} ${hours} ${getHourUnit(hours)})` : ` (${t('noCommunicationSince')} ${days} ${t('days')})`;
      }
    } else {
      const hours = Math.floor(diffInHours);
      return ` (${t('noCommunicationSince')} ${hours} ${getHourUnit(hours)})`;
    }
  };

  const formatLastTimestamp = (timestamp) => {
    if (!timestamp) return t('loading');
    const parsed = new Date(timestamp);
    if (Number.isNaN(parsed.getTime())) return timestamp;
    return parsed.toLocaleString();
  };

  const formatHoursValue = (value) => {
    if (value === undefined || value === null || value === '') {
      return t('loading');
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return value;
    }

    const abs = Math.abs(numericValue);
    let unit = t('hourUnit_many');

    if (language === 'pl') {
      const mod10 = abs % 10;
      const mod100 = abs % 100;
      if (abs === 1) {
        unit = t('hourUnit_one');
      } else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        unit = t('hourUnit_few');
      }
    } else if (abs === 1) {
      unit = t('hourUnit_one');
    }

    return `${numericValue} ${unit}`;
  };

  const getHourUnit = (value) => {
    const abs = Math.abs(Number(value));
    let unit = t('hourUnit_many');

    if (language === 'pl') {
      const mod10 = abs % 10;
      const mod100 = abs % 100;
      if (abs === 1) {
        unit = t('hourUnit_one');
      } else if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
        unit = t('hourUnit_few');
      }
    } else if (abs === 1) {
      unit = t('hourUnit_one');
    }

    return unit;
  };

  const hasProcessStats = chartData && chartData.ProcessStats;
  const hasDiagnostics = chartData && chartData.CurrentDiagnostics;

  // Show empty charts while loading data instead of loading message

  return (
    <div className={styles.dashboard} style={{ 
      display: 'flex', 
      flexDirection: isCompactLayout ? 'column' : 'row',
      minHeight: '100vh',
      height: isCompactLayout ? 'auto' : '100vh',
      alignItems: isCompactLayout ? 'stretch' : 'flex-start'
    }}>
      {renameModal && (
        <ModalDialog
          title={t('renameFilter')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitRenameSelectedFilter}
          onClose={() => setRenameModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('renameFilter')}</label>
              <input
                type="text"
                value={renameModal.value}
                onChange={(e) => setRenameModal((prev) => ({ ...prev, value: e.target.value, error: '' }))}
                placeholder={t('renameFilterPrompt')}
              />
            </div>
            {renameModal.error && <p className={styles.modalError}>{renameModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {messageModal && (
        <ModalDialog
          title={messageModal.title}
          message={messageModal.message}
          confirmLabel={t('close')}
          cancelLabel={t('close')}
          hideConfirm
          onConfirm={() => setMessageModal(null)}
          onClose={() => setMessageModal(null)}
        />
      )}
      {passwordModal && (
        <ModalDialog
          title={t('changePassword')}
          confirmLabel={t('save')}
          cancelLabel={t('cancel')}
          onConfirm={submitPasswordChange}
          onClose={() => setPasswordModal(null)}
        >
          <div className={styles.modalForm}>
            <div className={styles.modalField}>
              <label>{t('newPasswordPrompt')}</label>
              <input
                type="password"
                value={passwordModal.password}
                onChange={(e) => setPasswordModal((prev) => ({ ...prev, password: e.target.value, error: '' }))}
                placeholder={t('newPasswordPrompt')}
              />
            </div>
            <div className={styles.modalField}>
              <label>{t('confirmPasswordPrompt')}</label>
              <input
                type="password"
                value={passwordModal.confirmPassword}
                onChange={(e) => setPasswordModal((prev) => ({ ...prev, confirmPassword: e.target.value, error: '' }))}
                placeholder={t('confirmPasswordPrompt')}
              />
            </div>
            {passwordModal.error && <p className={styles.modalError}>{passwordModal.error}</p>}
          </div>
        </ModalDialog>
      )}
      {/* Sidebar */}
      <div className={styles.sidebar} style={{
        width: isCompactLayout ? '100%' : '250px',
        maxWidth: isCompactLayout ? 'none' : '250px',
        height: isCompactLayout ? 'auto' : '100vh',
        minHeight: isCompactLayout ? 'auto' : 'unset',
        maxHeight: isCompactLayout ? 'none' : '100vh',
        order: isCompactLayout ? 2 : 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: isCompactLayout ? 'visible' : 'auto'
      }}>
        <div className={styles.userInfo}>
          <h3 style={{ 
            fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)', 
            lineHeight: '1.2',
             wordWrap: 'break-word'
          }}>{t('userInfo')}</h3>
        </div>
        <div className={styles.deviceList}>
          <h4>{t('devices')}</h4>
          <ul>
            {displayDevices.map((device) => (
              <li
                key={device.id}
                className={currentSelectedDevice === device.id ? styles.activeDevice : ''}
                onClick={() => devices.length > 0 && setSelectedDevice(device.id)}
              >
                {device.name}
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.languageSelector}>
          <h4>{t('language')}</h4>
          <div className={styles.languageFlags}>
            <span
              className={`fi fi-gb ${language === 'en' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('en')}
              title="English"
            ></span>
            <span
              className={`fi fi-pl ${language === 'pl' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('pl')}
              title="Polski"
            ></span>
            <span
              className={`fi fi-de ${language === 'de' ? styles.activeFlag : ''}`}
              onClick={() => setLanguage('de')}
              title="Deutsch"
            ></span>
          </div>
        </div>
        {me?.is_superuser && (
          <button className={styles.logoutButton} onClick={() => navigate('/admin')}>
            {t('adminPanel')}
          </button>
        )}
        {selectedDevice !== null && !me?.is_superuser && (
          <button className={styles.secondaryActionButton} onClick={renameSelectedFilter}>
            {t('renameFilter')}
          </button>
        )}
        {!me?.is_superuser && (
          <button className={styles.secondaryActionButton} onClick={() => navigate('/settings')}>
            {t('notificationSettings')}
          </button>
        )}
        {!me?.is_superuser && (
          <button className={styles.secondaryActionButton} onClick={openPasswordModal}>
            {t('changePassword')}
          </button>
        )}
        <button className={styles.logoutButton} onClick={logout}>
          {t('logout')}
        </button>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent} style={{
        flex: 1,
        padding: isCompactLayout ? '12px' : '20px',
        order: isCompactLayout ? 1 : 2
      }}>
        <div 
          className={styles.tiles} 
          style={{
            display: 'grid',
            gridTemplateColumns: isCompactLayout 
              ? '1fr' 
              : '1fr 1fr',
            gridTemplateRows: isCompactLayout 
              ? 'auto' 
              : '1fr 1fr',
            gap: isCompactLayout ? '14px' : '20px',
            height: isCompactLayout ? 'auto' : 'calc(100vh - 120px)'
          }}
        >
          {/* Widok zaawansowany */}
          {/*advancedView && (
            <div className={styles.tile}>
              <LineChartComponent data={chartData.DataStatus} name={'filter_status'} statelabels={StateLabels} />
            </div>
          )*/}
          <div className={styles.tile} style={isCompactLayout ? { minHeight: '320px' } : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('hepa_filter')}</h3>
              <div style={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <GaugeChartComponent hepaValue={currentFilterUsage} />
              </div>
            </div>
          </div>
          {/*advancedView && (
            <div className={styles.tile}>
              <LineChartComponent data={chartData.DataPressure} name={'filter_pressure'} />
            </div>
          )*/}
          <div className={styles.tile} style={isCompactLayout ? { minHeight: '360px', overflow: 'visible' } : { overflow: 'visible' }}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('process_times')}</h3>
              <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
                <PieChartComponent chartdata={chartData.DataTimes || {}} />
              </div>
            </div>
          </div>
          <div className={styles.tile} style={isCompactLayout ? { minHeight: '340px' } : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t(trendChartTitleKey)}</h3>
              <div style={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                <LineChartComponent
                  data={trendChartData}
                  regressionData={trendChartRegression}
                  name={trendChartTitleKey}
                />
              </div>
            </div>
          </div>
          {isAdmin && (
            <div className={styles.tile} style={isCompactLayout ? { minHeight: '340px' } : undefined}>
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('daily_completed_processes')}</h3>
                <div style={{ flex: 1, overflow: 'hidden', width: '100%' }}>
                  <LineChartComponent
                    data={dailyCompletedProcessData}
                    name="daily_completed_processes"
                    timeUnit="hour"
                    stepped
                  />
                </div>
              </div>
            </div>
          )}
          <div className={styles.tile} style={isCompactLayout ? { minHeight: '260px' } : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>{t('device_statistics')}</h3>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '8px', 
                flex: 1,
                fontSize: 'clamp(0.9rem, 2vw, 1.1rem)',
                justifyContent: 'center'
              }}>
                <div>{t(`allTime`)}: {formatHoursValue(chartData.DataAllTime)}</div>
                <div>{t(`fanTime`)}: {formatHoursValue(chartData.DataFanTime)}</div>
                {hasProcessStats && <div>{t('completedProcesses')}: {chartData.ProcessStats.completed}</div>}
                {hasProcessStats && <div>{t('recognizedProcesses')}: {chartData.ProcessStats.recognized}</div>}
                {hasProcessStats && <div>{t('unrecognizedProcesses')}: {chartData.ProcessStats.unrecognized}</div>}
                {hasProcessStats && <div>{t('recognitionRate')}: {chartData.ProcessStats.recognition_rate}%</div>}
                <div style={{ 
                  color: isOlderThan6Hours(chartData.Last) ? '#e40000ff' : 'inherit'
                }}>{t(`last`)}: {formatLastTimestamp(chartData.Last)}{getTimeSinceLastCommunication(chartData.Last)}</div>
                <div>{t('lastReadStatus')}: {chartData.DataStatusNow !== undefined ? StateLabels[chartData.DataStatusNow] || t('unknown') : t('loading')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
